import React from 'react';
import { Button, EmptyState, ErrorState, FilterChip, SearchInput } from '@parsina/framework';
import { Activity, SearchX, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { useShortcut } from '../../lib/shortcuts';
import { servicesView } from '../../lib/aiPricingView';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';
import { SERVICES_QUERY } from './queries';
import ServicesTable from './ServicesTable';
import { isIrtCredit } from '../../lib/format';
import ServiceDrawer from './ServiceDrawer';
import { CsvExportButton, TableSkeleton, useServiceLabel } from './shared';

const CHIPS = ['negative', 'unpriced', 'mismatch'];

/**
 * View «سرویس‌ها» — Archetype B (list), the heart of the page (design §12.5).
 *
 * One call returns every service with its window aggregates; narrowing,
 * sorting and the three chips are local and pure (`servicesView`). The money
 * in the Toman column is converted by the ACTIVE policy's FX — the header line
 * under the toolbar says which, because a Toman figure with an unstated rate
 * is a number nobody can check.
 */
export default function ServicesView({ window, view, go }) {
  const { t, formatDigits } = useTranslation();
  const label = useServiceLabel();
  const searchRef = React.useRef(null);

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(SERVICES_QUERY, { window })
      .then((d) => setData(d?.aiServices || { services: [] }))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [window, t]);

  const first = React.useRef(true);
  React.useEffect(() => {
    load({ quiet: !first.current });
    first.current = false;
  }, [load]);

  const rows = data?.services || [];
  const result = React.useMemo(() => servicesView(rows, view, { label }), [rows, view, label]);

  const [draft, setDraft] = React.useState(view.q || '');
  React.useEffect(() => { setDraft(view.q || ''); }, [view.q]);
  React.useEffect(() => {
    if (draft === (view.q || '')) return undefined;
    const timer = setTimeout(() => go({ q: draft }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, view.q, go]);

  const focusSearch = () => { searchRef.current?.focus(); searchRef.current?.select?.(); };
  useShortcut('/', focusSearch, { label: t('shortcuts.action.search'), group: t('shortcuts.groups.aiPricing') });

  const onSort = (key) => {
    const current = view.sort || 'cost';
    const dir = view.dir || 'desc';
    if (current !== key) go({ sort: key, dir: 'desc' });
    else if (dir === 'desc') go({ dir: 'asc' });
    else go({ sort: '', dir: '' });
  };

  const clear = () => go({ q: '', negative: '', unpriced: '', mismatch: '' });

  const csvColumns = [
    { key: 'serviceKey', header: t('aiPricing.services.columns.service'), value: (r) => r.serviceKey },
    { key: 'label', header: t('aiPricing.services.columns.service'), value: (r) => label(r) },
    { key: 'invocations', header: t('aiPricing.services.columns.invocations'), value: (r) => r.invocations },
    { key: 'chargedRate', header: t('aiPricing.services.chargedRate', { pct: '' }).trim(), value: (r) => r.chargedRate },
    { key: 'avgIn', header: t('aiPricing.services.drawer.columns.avgIn'), value: (r) => r.avgInputTokens },
    { key: 'avgOut', header: t('aiPricing.services.drawer.columns.avgOut'), value: (r) => r.avgOutputTokens },
    { key: 'meanCostUsd', header: t('aiPricing.services.columns.costUsd'), value: (r) => r.meanCostUsd },
    isIrtCredit(data?.creditCurrency)
      ? { key: 'meanCostToman', header: t('aiPricing.services.columns.costToman'), value: (r) => r.meanCostToman }
      : null,
    { key: 'effectiveParseh', header: t('aiPricing.services.columns.price'), value: (r) => r.effectiveParseh },
    { key: 'priceSource', header: t('aiPricing.ledger.columns.source'), value: (r) => r.priceSource },
    { key: 'marginPct', header: t('aiPricing.services.columns.margin'), value: (r) => r.marginPct },
    { key: 'declared', header: t('aiPricing.services.columns.declared'), value: (r) => r.declaredInAppsParseh },
  ].filter(Boolean);

  const countLabel = result.isFiltered
    ? t('aiPricing.services.countFiltered', { count: result.filtered, total: result.total })
    : t('aiPricing.services.count', { count: result.total });

  return (
    <div data-testid="ai-services">
      <div className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid="ai-services-toolbar">
        <div role="group" aria-label={t('aiPricing.services.filters.label')} className="flex flex-wrap items-center gap-1.5" data-testid="ai-services-filter">
          {CHIPS.map((chip) => (
            <FilterChip
              key={chip}
              label={t(`aiPricing.services.filters.${chip}`)}
              active={view[chip] === '1'}
              onClick={() => go({ [chip]: view[chip] === '1' ? '' : '1' })}
              data-testid={`ai-services-filter-${chip}`}
            />
          ))}
        </div>
        <div className="min-w-[16rem] flex-1">
          <SearchInput
            ref={searchRef}
            value={draft}
            onChange={(e) => setDraft(e.target?.value ?? e)}
            onKeyDown={(e) => { if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); } }}
            placeholder={t('aiPricing.services.search')}
            aria-label={t('aiPricing.services.search')}
            data-testid="ai-services-search"
          />
        </div>
        {result.isFiltered ? (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="ai-services-clear">
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}
        <CsvExportButton columns={csvColumns} rows={result.rows} data-testid="ai-services-csv" />
      </div>
      <p className="mb-3 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground" aria-live="polite" data-testid="ai-services-count">
        <span>{countLabel}</span>
        {data && !loading && !error ? (
          <span data-testid="ai-services-rate">
            {data.policyId ? t('aiPricing.atRate', { name: formatDigits(data.policyId) }) : t('aiPricing.noRate')}
          </span>
        ) : null}
      </p>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: refreshing ? 0.45 : 1 }} aria-busy={refreshing || undefined}>
        {loading ? (
          <TableSkeleton cols={9} label={t('aiPricing.services.loading')} data-testid="ai-services-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.services.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-services-error"
          />
        ) : (
          <ServicesTable
            rows={result.rows}
            creditCurrency={data?.creditCurrency}
            view={view}
            onSort={onSort}
            onOpen={(r) => go({ service: r.serviceKey })}
            empty={result.isFiltered ? (
              <EmptyState
                icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.services.emptyFiltered.title')}
                description={t('aiPricing.services.emptyFiltered.desc')}
                action={<Button variant="outline" onClick={clear}>{t('common.actions.clearFilters')}</Button>}
                data-testid="ai-services-empty"
              />
            ) : (
              <EmptyState
                icon={<Activity size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.services.empty.title')}
                description={t('aiPricing.services.empty.desc')}
                data-testid="ai-services-empty"
              />
            )}
          />
        )}
      </div>

      {view.service ? (
        <ServiceDrawer
          serviceKey={view.service}
          window={window}
          onClose={() => go({ service: '' })}
          onClonePolicy={(policyId) => go({ view: 'policies', service: '', policy: 'new', from: policyId || '' })}
        />
      ) : null}
    </div>
  );
}
